import { useAddCell } from '@/views/editor/composable/table/useAddCell.ts'
import { useDeleteCell } from '@/views/editor/composable/table/useDeleteCell.ts'
import { useEditorStore } from '@/stores/editor.ts'
import { storeToRefs } from 'pinia'
import { useMergeTable } from '@/views/editor/composable/table/useMergeTable.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { useSelectorOverlay } from '@/views/editor/composable/table/selector/selctorOverlay.ts'

function isCellWithinSelectionBounds(cell: HTMLElement, dragListValue: any[], grid: any[]): boolean {
	if (!grid || grid.length === 0 || dragListValue.length < 2) return false

	function findPos(target: HTMLElement): { row: number; col: number } | null {
		for (let r = 0; r < grid.length; r++) {
			for (let c = 0; c < grid[r].length; c++) {
				if (grid[r][c]?.html === target) {
					return { row: r, col: c }
				}
			}
		}
		return null
	}

	const positions: { row: number; col: number }[] = []
	for (const item of dragListValue) {
		const pos = findPos(item)
		if (pos) positions.push(pos)
	}
	if (positions.length < 2) return false

	const minRow = Math.min(...positions.map(p => p.row))
	const maxRow = Math.max(...positions.map(p => p.row))
	const minCol = Math.min(...positions.map(p => p.col))
	const maxCol = Math.max(...positions.map(p => p.col))

	const cellPos = findPos(cell)
	if (!cellPos) return false

	return cellPos.row >= minRow && cellPos.row <= maxRow && cellPos.col >= minCol && cellPos.col <= maxCol
}

export function useContextMenu() {
	function open(event: any) {
		const useEditor = useEditorStore()
		const { cellTarget, dragList, tableSelector } = storeToRefs(useEditor)
		const { target } = event
		const checkTarget = target.closest('td')
		if (checkTarget) {
			let isInDragList = dragList.value.includes(checkTarget)

			if (!isInDragList && dragList.value.length > 1 && tableSelector.value) {
				isInDragList = isCellWithinSelectionBounds(checkTarget, dragList.value, tableSelector.value)
			}

			cellTarget.value = checkTarget

			if (!isInDragList) {
				const cellDiv = checkTarget.querySelector('div[contenteditable]')
				if (cellDiv) {
					const { positionSelector } = useSelectorOverlay()
					positionSelector(cellDiv)
				}
			}

			const contextMenu: HTMLDivElement | any = document.querySelector('.context--menu')
			const contextMenuContainer: HTMLDivElement | any = document.querySelector('.context--menu__container')
		contextMenu.classList.add('active')

			let topPosition = event.clientY
			let leftPosition = event.clientX

			const menuHeight = contextMenuContainer.offsetHeight
			const viewportHeight = window.innerHeight
			if (topPosition + menuHeight > viewportHeight) {
				topPosition = viewportHeight - menuHeight - 20
			}

			contextMenuContainer.style.position = 'absolute'
			contextMenuContainer.style.top = `${topPosition}px`
			contextMenuContainer.style.left = `${leftPosition}px`
		}
	}

	function close() {
		const { isContextMenu } = storeToRefs(useEditorStore())
		isContextMenu.value = false
		const contextMenu: HTMLDivElement | any = document.querySelector('.context--menu')
		contextMenu.classList.remove('active')
	}

	function add(type: string) {
		const { addCell } = useAddCell()
		addCell(type)
		close()
	}

	function merge() {
		useMergeTable().merge()
		close()
	}

	function unmerge() {
		useMergeTable().unmerge()
		close()
	}

	function unmergeTarget(target: any) {
		useMergeTable().unmergeTarget(target)
	}

	function remove(type: 'row' | 'column') {
		const { deleteCell } = useDeleteCell()
		deleteCell(type)
		close()
	}

	return {
		open,
		close,
		add,
		merge,
		unmerge,
		unmergeTarget,
		remove,
	}
}
